import React from 'react';

const ESTIMATED_ROW_HEIGHT = 48;
const OVERSCAN_PX = 400;
// jsdom and a not-yet-laid-out container report 0; render a first screenful instead of nothing.
const FALLBACK_VIEWPORT_HEIGHT = 640;

type VirtualWindow = {
  start: number;
  end: number;
  paddingTop: number;
  paddingBottom: number;
  observer: ResizeObserver | null;
};

export const useVirtualRows = (keys: string[], scrollRef: React.RefObject<HTMLElement>): VirtualWindow => {
  const heights = React.useRef(new Map<string, number>());
  const [observer, setObserver] = React.useState<ResizeObserver | null>(null);
  const [, remeasure] = React.useReducer((count: number) => count + 1, 0);
  const [scrollTop, setScrollTop] = React.useState(0);
  const [viewportHeight, setViewportHeight] = React.useState(0);
  const anchor = React.useRef<{ key: string; top: number } | null>(null);

  React.useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) {
      return undefined;
    }

    const onScroll = (): void => setScrollTop(container.scrollTop);
    const viewportObserver = new ResizeObserver(() => setViewportHeight(container.clientHeight));
    const rowObserver = new ResizeObserver((entries) => {
      let changed = false;
      entries.forEach(({ target }) => {
        const key = (target as HTMLElement).dataset.rowKey;
        const height = (target as HTMLElement).offsetHeight;
        if (key && height && heights.current.get(key) !== height) {
          heights.current.set(key, height);
          changed = true;
        }
      });
      if (changed) {
        remeasure();
      }
    });

    setViewportHeight(container.clientHeight);
    setObserver(rowObserver);
    container.addEventListener('scroll', onScroll, { passive: true });
    viewportObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', onScroll);
      viewportObserver.disconnect();
      rowObserver.disconnect();
    };
  }, []);

  const windowTop = scrollTop - OVERSCAN_PX;
  const windowBottom = scrollTop + (viewportHeight || FALLBACK_VIEWPORT_HEIGHT) + OVERSCAN_PX;

  let offset = 0;
  let start = keys.length;
  let end = -1;
  let paddingTop = 0;
  let renderedBottom = 0;
  const tops: number[] = [];

  keys.forEach((key, index) => {
    const height = heights.current.get(key) ?? ESTIMATED_ROW_HEIGHT;
    tops.push(offset);
    if (offset + height > windowTop && offset < windowBottom) {
      if (start > index) {
        start = index;
        paddingTop = offset;
      }
      end = index;
      renderedBottom = offset + height;
    }
    offset += height;
  });

  // Pin the first visible row as rows above it get measured or prepended; at the top, stay put so Load more rows show.
  React.useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }
    const previous = anchor.current;
    const previousIndex = previous ? keys.indexOf(previous.key) : -1;
    if (previous && previousIndex >= 0 && container.scrollTop > 0) {
      container.scrollTop += tops[previousIndex] - previous.top;
    }
    const index = tops.findIndex((_, i) => (tops[i + 1] ?? offset) > container.scrollTop);
    anchor.current = index >= 0 ? { key: keys[index], top: tops[index] } : null;
  });

  return { start, end, paddingTop, paddingBottom: offset - renderedBottom, observer };
};
